import { Activity, Bot, CircleAlert, CircleCheck, CircleX, Gauge, ShieldX, TriangleAlert } from '@orgus/ui/icons';
import { useState } from 'react';
import type { ReactNode } from 'react';
import type { AiActivity, AiAgentSummary, AiOutcome, AiState } from '@orgus/protocol';
import { Badge, Button, EmptyState, ErrorState, Panel, RelativeTime, Skeleton, StatusDot, TONE_CLASS, Tooltip, cn, plural } from '@orgus/ui';
import { NARROW_QUERY, useMediaQuery } from '../../lib/useMediaQuery.ts';
import { OUTCOME_LABEL, OUTCOME_TONE, TIER_TONE, activityDetail, activityTitle, actorName, tierLabel, todaySummary } from './model.ts';
import type { Load } from './useAiData.ts';

const OUTCOME_ICON: Record<AiOutcome, ReactNode> = {
  ok: <CircleCheck />,
  error: <CircleX />,
  refused: <ShieldX />,
  limited: <Gauge />,
  invalid: <CircleAlert />,
};

function Outcome({ outcome }: { outcome: AiOutcome }) {
  const tone = OUTCOME_TONE[outcome];
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-1 text-sm [&_svg]:size-3.5', tone !== 'neutral' && 'font-medium', TONE_CLASS[tone].fg)}>
      {OUTCOME_ICON[outcome]}
      {OUTCOME_LABEL[outcome]}
    </span>
  );
}

function LiveSub({ live, stale, at }: { live: boolean; stale?: boolean; at?: number }) {
  if (stale) {
    return (
      <span className="inline-flex items-center gap-1.5 text-waiting">
        <TriangleAlert className="size-3.5" aria-hidden />
        Not updating
        {at ? <RelativeTime ts={at} prefix="since" className="text-fg-subtle" /> : null}
      </span>
    );
  }
  return live ? (
    <span className="inline-flex items-center gap-1.5">
      <StatusDot tone="working" size="xs" label="" />
      Live
    </span>
  ) : null;
}

/** The live state at the header's end, after the title and its count. */
function LiveTrail(props: { live: boolean; stale?: boolean; at?: number }) {
  return (
    <span className="pr-2 text-ui text-fg-subtle">
      <LiveSub {...props} />
    </span>
  );
}

function RowsSkeleton({ rows, label }: { rows: number; label: string }) {
  return (
    <div role="status" aria-label={label} className="flex flex-col divide-y divide-border-subtle">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-4 w-14" />
        </div>
      ))}
    </div>
  );
}

function TierBadge({ a }: { a: AiAgentSummary }) {
  return (
    <Tooltip content="The highest tier this token reaches as a whole. Named scopes may allow single capabilities above it.">
      <span className="shrink-0">
        <Badge tone={TIER_TONE[a.ceiling]} variant="outline">
          Up to {tierLabel(a.ceiling).toLowerCase()}
        </Badge>
      </span>
    </Tooltip>
  );
}

/** Name on line one; the token and role under it. Below 768 px the tier joins that second line. */
function AgentRow({ a, narrow }: { a: AiAgentSummary; narrow: boolean }) {
  return (
    <li className="flex items-center gap-3 px-4 py-2.5">
      <span className="flex size-7 shrink-0 items-center justify-center rounded-md border border-border bg-raised text-fg-subtle [&_svg]:size-4" aria-hidden>
        <Bot />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-fg">{a.name}</p>
        <p className="flex min-w-0 items-center gap-2 text-sm text-fg-subtle">
          <span className="min-w-0 truncate">
            <span className="font-mono">{a.tokenId ?? a.id}</span>
            {!narrow && ` · ${a.role}`}
          </span>
          {narrow && <TierBadge a={a} />}
        </p>
      </div>
      {!narrow && <TierBadge a={a} />}
      <span className="hidden w-48 shrink-0 text-right text-sm text-fg-muted num md:block">{todaySummary(a)}</span>
      <span className="shrink-0 text-right text-sm text-fg-subtle md:w-24">
        <RelativeTime ts={a.lastSeenAt} />
      </span>
    </li>
  );
}

export function AgentsPanel({ load, live, retry }: { load: Load<AiState>; live: boolean; retry: () => Promise<void> }) {
  const agents = load.status === 'ready' ? load.data.agents : [];
  const narrow = useMediaQuery(NARROW_QUERY);
  return (
    <Panel
      title="Agents"
      icon={<Bot />}
      sub={load.status === 'ready' && agents.length ? agents.length : undefined}
      actions={load.status === 'ready' ? <LiveTrail live={live} stale={load.stale} at={load.at} /> : undefined}
      flush
    >
      {load.status === 'loading' ? (
        <RowsSkeleton rows={2} label="Loading agents" />
      ) : load.status === 'error' ? (
        <ErrorState error={load.error} onRetry={retry} />
      ) : agents.length === 0 ? (
        <EmptyState
          icon={<Bot />}
          title="No agent has connected yet"
          description="Agents appear here after their first call with an agent token, with what they read and changed today."
        />
      ) : (
        <ul className="flex flex-col divide-y divide-border-subtle" aria-label="Agents">
          {agents.map((a) => (
            <AgentRow key={a.id} a={a} narrow={narrow} />
          ))}
        </ul>
      )}
    </Panel>
  );
}

function TierChip({ a }: { a: AiActivity }) {
  return (
    <>
      <Badge tone={TIER_TONE[a.tier]} variant="outline" className="shrink-0">
        {tierLabel(a.tier)}
      </Badge>
      {a.direct ? (
        <Tooltip content="Sent straight to POST /api/actions rather than the AI API. Gated and rate limited the same way.">
          <span className="shrink-0">
            <Badge>Direct</Badge>
          </span>
        </Tooltip>
      ) : null}
    </>
  );
}

/**
 * One call. On a desktop: outcome, who and what with the tier, the time. Below 768 px it stacks
 * so nothing is cut: who and what (wrapping) on line one, the detail, then outcome, tier and time.
 */
function ActivityRow({ a, narrow }: { a: AiActivity; narrow: boolean }) {
  const detail = activityDetail(a);
  const who = (
    <>
      <span className="font-medium">{actorName(a.actor)}</span>
      <span className="text-fg-muted"> · {activityTitle(a)}</span>
    </>
  );
  if (narrow) {
    return (
      <li className="flex flex-col gap-1 px-4 py-3">
        <p className="text-fg">{who}</p>
        {detail ? <p className="text-sm text-fg-subtle">{detail}</p> : null}
        <p className="flex min-w-0 items-center gap-2">
          <Outcome outcome={a.outcome} />
          <TierChip a={a} />
          <RelativeTime ts={a.at} className="ml-auto shrink-0 text-sm text-fg-subtle" />
        </p>
      </li>
    );
  }
  return (
    <li className="flex items-start gap-3 px-4 py-2.5">
      <span className="w-28 shrink-0 pt-px">
        <Outcome outcome={a.outcome} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="flex min-w-0 items-center gap-2">
          <span className="truncate text-fg">{who}</span>
          <TierChip a={a} />
        </p>
        {detail ? <p className="mt-0.5 truncate text-sm text-fg-subtle" title={detail}>{detail}</p> : null}
      </div>
      <span className="w-24 shrink-0 text-right text-sm text-fg-subtle">
        <RelativeTime ts={a.at} />
      </span>
    </li>
  );
}

/** Rows shown before "Show all activity": the list ends on a row, never mid-line in a scroll box. */
const ACTIVITY_AT_REST = 6;

export function ActivityPanel({ load, live, retry }: { load: Load<AiState>; live: boolean; retry: () => Promise<void> }) {
  const recent = load.status === 'ready' ? load.data.recent : [];
  const limits = load.status === 'ready' ? load.data.limits : null;
  const narrow = useMediaQuery(NARROW_QUERY);
  const [all, setAll] = useState(false);
  const shown = all ? recent : recent.slice(0, ACTIVITY_AT_REST);
  return (
    <Panel
      title="Recent activity"
      icon={<Activity />}
      actions={load.status === 'ready' ? <LiveTrail live={live} stale={load.stale} at={load.at} /> : undefined}
      flush
    >
      {load.status === 'loading' ? (
        <RowsSkeleton rows={4} label="Loading recent activity" />
      ) : load.status === 'error' ? (
        <div className="p-3">
          <ErrorState error={load.error} onRetry={retry} variant="inline" />
        </div>
      ) : recent.length === 0 ? (
        <EmptyState
          icon={<Activity />}
          title="No calls yet"
          description="Agent reads and changes through the AI API show up here."
        />
      ) : (
        <>
          <ul className="flex flex-col divide-y divide-border-subtle" aria-label="Recent AI activity">
            {shown.map((a) => (
              <ActivityRow key={a.id} a={a} narrow={narrow} />
            ))}
          </ul>
          {recent.length > ACTIVITY_AT_REST && (
            <div className="border-t border-border-subtle px-2 py-1">
              <Button size="sm" variant="ghost" onClick={() => setAll((v) => !v)} aria-expanded={all}>
                {all ? 'Show fewer calls' : `Show all activity, ${plural(recent.length, 'call')}`}
              </Button>
            </div>
          )}
        </>
      )}
      {limits ? (
        <p className="border-t border-border-subtle px-4 py-2 text-sm text-fg-subtle num">
          Each agent may make {limits.readsPerMinute} reads and {limits.actionsPerMinute} changes a minute.
        </p>
      ) : null}
    </Panel>
  );
}
